import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { CopyButton } from './copy-button';
import { ModalTitle } from './modal-title';

/**
 * Delete confirmation: what's about to happen, then a confirm button.
 *
 * Pass confirmationText to additionally require typing the thing's own name
 * back — worth it for an irreversible delete, since in a grid of near-identical
 * rows it's easy to be looking at a different line than the one the button
 * belongs to. Leave it off where the delete can be undone.
 */

interface ConfirmDeleteModalProps {
  /**
   * When set, the confirm button stays disabled until this exact string is
   * typed. Omit for a plain message-and-confirm dialog.
   */
  confirmationText?: string;
  title: string;
  intro?: React.ReactNode;
  /** Consequences worth spelling out — shown as a list above the input. */
  warnings?: string[];
  confirmLabel?: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export const ConfirmDeleteModal = ({
  confirmationText,
  title,
  intro,
  warnings = [],
  confirmLabel = 'Delete',
  busy = false,
  onCancel,
  onConfirm,
}: ConfirmDeleteModalProps) => {
  const [typed, setTyped] = useState('');

  // A second delete opened straight after the first must start empty.
  useEffect(() => {
    setTyped('');
  }, [confirmationText]);

  const matches = !confirmationText || typed.trim() === confirmationText;

  return (
    <Dialog open onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <ModalTitle onClose={busy ? () => {} : onCancel}>{title}</ModalTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {intro && <div>{intro}</div>}

        {warnings.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {warnings.map((w) => (
              <li key={w}>
                <Typography variant="body2">{w}</Typography>
              </li>
            ))}
          </ul>
        )}

        {confirmationText && (
          <>
            <Typography variant="body2">
              Type the following to confirm:{' '}
              <Typography
                component="span"
                variant="body2"
                sx={{ fontFamily: 'monospace', fontWeight: 600 }}
              >
                {confirmationText}
              </Typography>
              <CopyButton size='medium' content={confirmationText} />
            </Typography>

            <TextField
              autoFocus
              fullWidth
              size="small"
              value={typed}
              disabled={busy}
              placeholder={confirmationText}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && matches && !busy) onConfirm();
              }}
              inputProps={{ spellCheck: false, autoComplete: 'off', style: { fontFamily: 'monospace' } }}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ pb: 3, px: 3 }}>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" color="error" disabled={!matches || busy} onClick={onConfirm}>
          {busy ? 'Working…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};